/**
 * DimensionalControlPanel — F5.8b refactor
 *
 * Panel de documentos REQUERIDOS por cada PieceInstance:
 *   - Control dimensional (PDF llenado a mano): obligatorio para piezas terminadas
 *   - Dossier de calidad: obligatorio si Piece.requiereDosier=true
 *
 * No usa formularios digitales (la tabla anterior fue eliminada). El operario
 * imprime, mide a mano, escanea y sube el PDF.
 */
import React, { useEffect, useRef, useState } from "react";
import backend from "../../backend";
import ControlDimensionalF2 from "./ControlDimensionalF2";

const DimensionalControlPanel = ({ pi, readOnly = false }) => {
  const [piece, setPiece] = useState(null);
  const [docs, setDocs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null); // controlDimensional | dosierCalidad
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);
  const pendingTypeRef = useRef(null);

  // Resolver pieceId (puede venir populado o como string)
  const pieceId = typeof pi.piece === "object" && pi.piece !== null
    ? (pi.piece._id || pi.piece.id || null)
    : pi.piece;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const reqs = [];
        if (pieceId) reqs.push(backend.get(`piece/${pieceId}`));
        else reqs.push(Promise.resolve({ data: null }));
        // Documentos del PI: vinculados por workOrderRefs (de la WO padre)
        if (pi.workOrder) {
          reqs.push(backend.get(`document?workOrderRefs=${pi.workOrder._id || pi.workOrder}`));
        } else {
          reqs.push(Promise.resolve({ data: [] }));
        }
        const [pieceRes, docsRes] = await Promise.all(reqs);
        if (!alive) return;
        setPiece(pieceRes.data);
        setDocs(docsRes.data || []);
      } catch (e) {
        // silencioso
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [pieceId, pi.workOrder]);

  const requiereDosier = !!piece?.requiereDosier;
  const esImportada    = !!piece?.esImportada;  // F5.8b — no requiere control dimensional
  const isFinished     = ["ready", "delivered", "invoiced"].includes(pi.status);

  // F5.8b — Reload docs after upload
  const reloadDocs = async () => {
    try {
      const woId = pi.workOrder?._id || pi.workOrder;
      if (!woId) return;
      const res = await backend.get(`document?workOrderRefs=${woId}`);
      setDocs(res.data || []);
    } catch (e) { /* silent */ }
  };

  // F5.8b — Upload directo
  const triggerUpload = (docType) => {
    pendingTypeRef.current = docType;
    setUploadError("");
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset para permitir re-subir mismo archivo
    if (!file) return;
    const docType = pendingTypeRef.current;
    if (!docType) return;
    const woId = pi.workOrder?._id || pi.workOrder;
    if (!woId) {
      setUploadError("PI sin workOrder asociada");
      return;
    }
    setUploadingType(docType);
    setUploadError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("docType", docType);
      fd.append("workOrderRefs", woId);
      fd.append("label", `${docType === "controlDimensional" ? "Control dim" : "Dossier"} — ${pi.pieceNameSnapshot || ""}`.slice(0, 80));
      await backend.post("document/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await reloadDocs();
    } catch (err) {
      setUploadError(err?.response?.data?.error || err.message);
    }
    setUploadingType(null);
    pendingTypeRef.current = null;
  };

  // Filtrar docs por tipo (los del WO sirven para todo el trabajo)
  const docsControl = docs.filter(d => d.docType === "controlDimensional" && !d.deletedAt);
  const docsDosier  = docs.filter(d => d.docType === "dosierCalidad"      && !d.deletedAt);

  const slot = ({ label, icon, type, docs: slotDocs, required, requiredReason }) => {
    const has = slotDocs.length > 0;
    const showWarning = required && !has;
    const accentColor = has ? "#2ecc71" : showWarning ? "#e74c3c" : "#666";
    const isUploadingThis = uploadingType === type;
    return (
      <div style={{
        padding: "8px 12px",
        background: showWarning ? "#1f0a0a" : "#161616",
        border: `1px solid ${showWarning ? "#4a1a1a" : "#2e2e2e"}`,
        borderRadius: 5,
        marginBottom: 6,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
            <span style={{ fontSize: 14 }}>{icon}</span>
            <span style={{ color: "#ddd", fontWeight: 600 }}>{label}</span>
            {required && (
              <span style={{
                fontSize: 9, fontWeight: 700,
                padding: "1px 6px", borderRadius: 8,
                background: accentColor + "22", color: accentColor,
                border: `1px solid ${accentColor}55`,
              }}>
                {has ? "✓ adjunto" : "⚠ requerido"}
              </span>
            )}
            {!required && (
              <span style={{ fontSize: 9, color: "#aaa" }}>(opcional)</span>
            )}
            {has && (
              <span style={{ fontSize: 10, color: "#cbd5e1" }}>· {slotDocs.length} archivo{slotDocs.length === 1 ? "" : "s"}</span>
            )}
          </div>
          {!readOnly && (
            <button
              onClick={() => triggerUpload(type)}
              disabled={isUploadingThis}
              style={{
                background: showWarning ? "#e74c3c22" : "#3498db22",
                border: `1px solid ${showWarning ? "#e74c3c55" : "#3498db55"}`,
                color: showWarning ? "#e74c3c" : "#3498db",
                borderRadius: 4, padding: "4px 10px",
                fontSize: 10, cursor: "pointer", fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title={`Subir ${label.toLowerCase()}`}
            >
              {isUploadingThis ? "Subiendo…" : has ? "+ Otro archivo" : "📎 Adjuntar"}
            </button>
          )}
        </div>
        {requiredReason && showWarning && (
          <div style={{ fontSize: 10, color: "#e74c3c", marginTop: 4, fontStyle: "italic" }}>
            {requiredReason}
          </div>
        )}
        {has && (
          <div style={{ marginTop: 6, fontSize: 10, color: "#aaa" }}>
            {slotDocs.slice(0, 3).map(d => (
              <div key={d._id} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                📎 <a href={d.url} target="_blank" rel="noreferrer" style={{ color: "#3498db", textDecoration: "none" }}>
                  {d.originalName}
                </a>
                {d.label && <span style={{ color: "#aaa" }}> — {d.label}</span>}
              </div>
            ))}
            {slotDocs.length > 3 && (
              <div style={{ color: "#aaa" }}>+ {slotDocs.length - 3} más en sección Documentos</div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        borderTop: "1px dashed #2e2e2e", background: "#0d0d0d",
        padding: "10px 14px", fontSize: 11, color: "#aaa",
      }}>
        Cargando docs requeridos…
      </div>
    );
  }

  return (
    <div style={{
      borderTop: "1px dashed #2e2e2e",
      background: "#0d0d0d",
      padding: "10px 14px",
    }}>
      {/* input file oculto compartido por ambos slots */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />
      {uploadError && (
        <div style={{
          padding: "5px 10px", marginBottom: 8,
          background: "#2d0000", border: "1px solid #4a0000",
          borderRadius: 4, color: "#e74c3c", fontSize: 10,
        }}>
          ⚠ {uploadError}
        </div>
      )}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 8, flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ fontSize: 10, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: ".06em" }}>
          📋 Documentos requeridos para esta pieza
        </div>
        {esImportada && (
          <span style={{
            fontSize: 9, fontWeight: 700,
            padding: "2px 8px", borderRadius: 9,
            background: "#16345222", color: "#3498db",
            border: "1px solid #16345255",
          }}>
            🌐 Pieza importada
          </span>
        )}
      </div>

      {/* F2 (31-ago): el control digital, atado a las cotas críticas del catálogo. Se
          dibuja solo si la pieza tiene cotas definidas; si no, esta pieza sigue con el
          PDF a mano de más abajo, que es lo que había y sigue sirviendo. */}
      {!esImportada && <ControlDimensionalF2 pi={pi} readOnly={readOnly} />}

      {!esImportada && slot({
        label: "Control dimensional",
        icon: "📐",
        type: "controlDimensional",
        docs: docsControl,
        required: isFinished,
        requiredReason: isFinished
          ? "Pieza terminada: el control dimensional debe estar adjunto antes de cerrar la OT."
          : null,
      })}

      {esImportada && (
        <div style={{
          padding: "8px 12px", background: "#0c1a2a",
          border: "1px solid #16345255", borderRadius: 5, marginBottom: 6,
          fontSize: 11, color: "#7ec8e3",
        }}>
          🌐 Esta pieza es importada (comprada hecha) — no requiere control dimensional
          de fabricación. Si tiene certificado del fabricante, súbelo en la sección
          Documentos como "Dossier de calidad".
        </div>
      )}

      {slot({
        label: "Dossier de calidad",
        icon: "📋",
        type: "dosierCalidad",
        docs: docsDosier,
        required: requiereDosier,
        requiredReason: requiereDosier
          ? "Pieza marcada como estructural: el dossier (incluyendo certificados de material) es obligatorio."
          : null,
      })}

      {!readOnly && (
        <div style={{ fontSize: 9, color: "#999", fontStyle: "italic", marginTop: 4 }}>
          También podés gestionar todos los documentos en la sección "Documentos del trabajo" más abajo.
        </div>
      )}
    </div>
  );
};

export default DimensionalControlPanel;
